import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  get as getRequest,
  remove as removeRequest,
  update as updateRequest,
  execute as executeRequest,
  getRuns as getRunsRequest,
  getPreview as getPreviewRequest,
} from './requests';
import { toast } from 'react-toastify';
import {
  PromptTestCase,
  PromptTestCaseRequest,
  PromptTestCaseRun,
  PromptTestCaseRunRequest,
  QueryKey,
} from 'api/model';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.PromptTestCases;

const usePromptTestCase = (promptId: string, id: string, promptRevision: number) => {
  const queryClient = useQueryClient();

  const { data: testCase, isLoading: modelIsLoading } = useQuery<PromptTestCase, Error>({
    queryKey: [queryKey, { promptId, id }],
    queryFn: () => getRequest(promptId, id),
  });

  const { data: runs, isLoading: areRunsLoading } = useQuery<PromptTestCaseRun[], Error>({
    queryKey: [queryKey, { promptId, id, promptRevision, runs: true }],
    queryFn: () => getRunsRequest(promptId, id, promptRevision),
    enabled: Boolean(promptId && id),
  });

  const update = useMutation({
    mutationFn: (testCase: PromptTestCaseRequest) => updateRequest(promptId, id, testCase),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey, { promptId }] });
      toast.success('Prompt test case successfully updated');
    },
    onError: errorHandler('Updating prompt test case failed'),
  });

  const remove = useMutation({
    mutationFn: () => removeRequest(promptId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey, { promptId }] });
      toast.success('Prompt test case successfully removed');
    },
    onError: errorHandler('Removing prompt test case failed'),
  });

  const execute = useMutation({
    mutationFn: (payload: PromptTestCaseRunRequest) => executeRequest(promptId, id, payload, promptRevision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey, { promptId, id }] });
      void queryClient.invalidateQueries({ queryKey: [queryKey, { promptId, id, promptRevision, runs: true }] });
      toast.success('Prompt test case successfully executed');
    },
    onError: errorHandler('Executing prompt test case failed'),
  });

  const getPreview = useMutation({
    mutationFn: (testCaseData: object) => getPreviewRequest(promptId, testCaseData, promptRevision),
    onError: errorHandler('Loading preview failed'),
  });

  return { testCase, modelIsLoading, remove, update, execute, getPreview, runs, areRunsLoading };
};

export default usePromptTestCase;
